import { useState } from 'react';
import { DynamicUserProfile, useDynamicContext } from '@dynamic-labs/sdk-react-core';

import { HAS_DYNAMIC_ENV } from '../WalletProvider';
import { ImportKeyDialog } from './ImportKeyDialog';

export function WalletButton() {
  const [importing, setImporting] = useState(false);

  if (!HAS_DYNAMIC_ENV) {
    return <span className="net-status mute">Wallet not configured</span>;
  }

  return (
    <>
      <ImportKeyShortcut onOpen={() => setImporting(true)} />
      <SessionPill />
      {/*
        Mount Dynamic's user-profile modal standalone so our custom
        SessionPill can flip it open via setShowDynamicUserProfile.
        Without this, the modal has no consumer (it normally lives
        inside <DynamicWidget />, which we replaced).
      */}
      <DynamicUserProfile />
      <ImportKeyDialog open={importing} onOpenChange={setImporting} />
    </>
  );
}

/**
 * Custom HUD-styled trigger that replaces `<DynamicWidget />`. Dynamic's
 * built-in pill wins specificity wars against our cssOverrides for layout
 * (border-radius, height, font), so we render our own button and route
 * its clicks through Dynamic's auth-flow / user-profile modals — those
 * still inherit the cssOverrides we already configured.
 */
function SessionPill() {
  const ctx = useDynamicContext();
  const { sdkHasLoaded, primaryWallet, setShowAuthFlow, setShowDynamicUserProfile } = ctx;

  if (!sdkHasLoaded) {
    return (
      <button type="button" className="sly-btn" disabled>
        ···
      </button>
    );
  }

  if (!primaryWallet) {
    return (
      <button type="button" className="sly-btn" onClick={() => setShowAuthFlow(true)}>
        Connect
      </button>
    );
  }

  const addr = primaryWallet.address ?? '';
  const short = addr.length > 8 ? `${addr.slice(0, 4)}…${addr.slice(-4)}` : addr;
  return (
    <button
      type="button"
      className="sly-btn wallet-pill"
      onClick={() => setShowDynamicUserProfile(true)}
      title={addr}
    >
      <span className="wallet-pill-dot" aria-hidden />
      <span className="wallet-pill-addr">{short}</span>
    </button>
  );
}

/**
 * Surface the "Import key" trigger only once Dynamic is ready AND the user
 * is signed in — `useDynamicWaas().importPrivateKey` requires an
 * authenticated session, so showing the button before sign-in just leads
 * to a confusing error.
 */
function ImportKeyShortcut({ onOpen }: { onOpen: () => void }) {
  const { sdkHasLoaded, primaryWallet } = useDynamicContext();
  if (!sdkHasLoaded || !primaryWallet) return null;
  return (
    <button type="button" className="sly-btn" onClick={onOpen}>
      Import key
    </button>
  );
}
